import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fdate, useApi } from "@/lib/api";

const RED = "#d32f2f";
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Head({ d, title }) {
  return (
    <div className="flex justify-between items-start border-b pb-4 mb-4" style={{ borderColor: RED }}>
      <div><img src="/brand/gn-logo.png" alt="Gamerz Nation" className="h-14" /><div className="text-[10px] whitespace-pre-line mt-1">{d.company.address}{"\n"}{d.company.phone}</div>
        {d.company.gstin && <div className="text-[11px] font-semibold mt-1">GSTIN: {d.company.gstin}</div>}</div>
      <div className="text-right"><div className="text-xl font-bold" style={{ color: RED }}>{title}</div>
        <div className="text-xs mt-1">No: <b data-testid="b2b-doc-number">{d.invoiceNumber || d.orderNumber}</b></div>
        <div className="text-xs">Date: {fdate(d.invoicedAt || d.createdAt)}</div>{d.poNumber && <div className="text-xs">PO: {d.poNumber}</div>}</div>
    </div>
  );
}

function Bill({ c }) {
  return (
    <div className="text-xs mb-4" data-testid="b2b-doc-client"><div className="text-zinc-500">Bill to</div><div className="font-semibold text-sm">{c.companyName}</div>
      <div className="font-mono" data-testid="b2b-doc-client-gstin">GSTIN: {c.gstin || "Unregistered"}</div><div className="whitespace-pre-line">{c.billingAddress}</div>
      <div>{c.contactPerson} {c.phone}</div><div>Terms: {c.creditTerms}</div></div>
  );
}

function Invoice({ d }) {
  const rows = [["Taxable value", d.taxableTotal], ...(d.intraState ? [["CGST", d.cgst], ["SGST", d.sgst]] : [["IGST", d.igst]]), ["Total payable", d.grandTotal]];
  return (
    <>
      <table className="w-full text-xs border-collapse mb-3"><thead><tr style={{ color: RED }}>{["#", "Description", "Qty", "Unit price", "GST %", "Taxable"].map((h) => <th key={h} className="border px-2 py-1 text-left">{h}</th>)}</tr></thead>
        <tbody>{d.lines.map((l, i) => <tr key={l.id}><td className="border px-2 py-1">{i + 1}</td><td className="border px-2 py-1">{l.description}</td><td className="border px-2 py-1">{l.quantity}</td>
          <td className="border px-2 py-1">{money(l.unitPrice)}</td><td className="border px-2 py-1">{l.gstPercent}%</td><td className="border px-2 py-1">{money(l.quantity * l.unitPrice)}</td></tr>)}</tbody></table>
      <div className="ml-auto w-64 text-xs space-y-1" data-testid="b2b-doc-totals">{rows.map(([k, v]) => <div key={k} className={`flex justify-between ${k === "Total payable" ? "font-bold text-sm border-t pt-1" : ""}`}><span>{k}</span><span data-testid={`b2b-doc-${k.split(" ")[0].toLowerCase()}`}>{money(v)}</span></div>)}</div>
      <div className="text-[11px] mt-6">Bank: {d.company.banks.map((b) => `${b.bank} A/C ${b.accountNumber} IFSC ${b.ifsc}`).join(" | ")}</div>
    </>
  );
}

function Packing({ d }) {
  return (
    <table className="w-full text-xs border-collapse"><thead><tr style={{ color: RED }}>{["#", "Description", "Qty", "Packed ✓"].map((h) => <th key={h} className="border px-2 py-1 text-left">{h}</th>)}</tr></thead>
      <tbody>{d.lines.map((l, i) => <tr key={l.id}><td className="border px-2 py-1">{i + 1}</td><td className="border px-2 py-1">{l.description}</td><td className="border px-2 py-1">{l.quantity}</td><td className="border px-2 py-1" /></tr>)}
        <tr><td className="border px-2 py-1" colSpan={2}><b>Total units</b></td><td className="border px-2 py-1"><b>{d.totalUnits}</b></td><td className="border" /></tr></tbody></table>
  );
}

export default function B2BDocument() {
  const { oid } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { data: d } = useApi(`/b2b/orders/${oid}/document`);
  const packing = sp.get("doc") === "packing";
  const title = packing ? "PACKING SLIP" : d?.invoiceNumber ? "TAX INVOICE" : "PROFORMA INVOICE";
  return (
    <div>
      <div className="flex gap-2 mb-4 no-print"><Button variant="ghost" onClick={() => nav(-1)} data-testid="b2b-doc-back"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
        <Button className="ml-auto bg-[#d32f2f] hover:bg-[#b71c1c] text-white" onClick={() => window.print()} data-testid="b2b-doc-print"><Printer className="h-4 w-4 mr-2" />Print / Download PDF</Button></div>
      {d && <div className="print-area bg-white text-zinc-900 max-w-[794px] mx-auto p-8 shadow-sm" data-testid="b2b-document"><Head d={d} title={title} /><Bill c={d.client} />{packing ? <Packing d={d} /> : <Invoice d={d} />}
        {packing && <div className="grid grid-cols-2 gap-8 mt-12 text-xs"><div className="border-t pt-1">Packed by</div><div className="border-t pt-1">Received by (sign & stamp)</div></div>}</div>}
    </div>
  );
}
